<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-lg font-semibold text-gray-900">Fournisseurs</h1>
        <p class="text-xs text-gray-400">Gérez vos partenaires et fournisseurs</p>
      </div>
      <button
        v-permission="'suppliers.create'"
        class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors"
        @click="onAddSupplier">
        <i class="fas fa-plus text-[10px]"></i>
        Nouveau fournisseur
      </button>
    </div>

    <!-- Card principale -->
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <!-- Toolbar -->
      <div class="px-5 py-3 border-b bg-gray-50/80 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div class="relative flex-1 max-w-sm">
          <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-[10px]"></i>
          <input
            type="text"
            v-model="filters.search"
            @input="debouncedFetch"
            placeholder="Rechercher par nom, email, téléphone..."
            class="w-full pl-8 pr-8 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder-gray-300"
          />
          <button v-if="filters.search" @click="clearSearch"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 transition-colors">
            <i class="fas fa-times text-[9px]"></i>
          </button>
        </div>
        <button @click="fetchSuppliers" title="Actualiser"
          class="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors">
          <i class="fas fa-sync text-[10px]"></i>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <span class="w-8 h-8 border-3 border-gray-200 border-t-primary rounded-full animate-spin mb-3"></span>
        <p class="text-xs text-gray-400">Chargement des fournisseurs...</p>
      </div>

      <div v-else>
        <!-- Empty state -->
        <div v-if="suppliers.length === 0" class="py-20 text-center">
          <div class="flex flex-col items-center">
            <div class="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center mb-4">
              <i class="fas fa-users text-gray-300 text-xl"></i>
            </div>
            <h3 class="text-sm font-semibold text-gray-700 mb-1">Aucun fournisseur trouvé</h3>
            <p v-if="filters.search" class="text-xs text-gray-400 mb-4">Essayez de modifier vos critères de recherche</p>
            <p v-else class="text-xs text-gray-400 mb-4">Commencez par ajouter votre premier fournisseur</p>
            <button v-if="!filters.search"
              v-permission="'suppliers.create'"
              @click="onAddSupplier"
              class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors">
              <i class="fas fa-plus text-[10px]"></i>
              Ajouter le premier fournisseur
            </button>
          </div>
        </div>

        <!-- Table -->
        <div v-else>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-gray-100">
                  <th class="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Fournisseur</th>
                  <th class="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Contact</th>
                  <th class="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Téléphone</th>
                  <th class="px-5 py-3 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Statut</th>
                  <th class="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Créé le</th>
                  <th class="px-5 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="supplier in suppliers" :key="supplier.id"
                  class="border-b border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer group"
                  @click="goToDetails(supplier.id)">
                  <!-- Fournisseur -->
                  <td class="px-5 py-3">
                    <div class="flex items-center gap-3">
                      <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span class="text-[11px] font-bold text-primary">{{ getInitials(supplier.name) }}</span>
                      </div>
                      <div>
                        <p class="text-sm font-medium text-gray-900">{{ supplier.name }}</p>
                        <p v-if="supplier.contact_person" class="text-[10px] text-gray-400 mt-0.5">{{ supplier.contact_person }}</p>
                      </div>
                    </div>
                  </td>
                  <!-- Email -->
                  <td class="px-5 py-3">
                    <div v-if="supplier.email" class="flex items-center gap-2">
                      <i class="fas fa-envelope text-gray-300 text-[9px]"></i>
                      <span class="text-xs text-gray-600">{{ supplier.email }}</span>
                    </div>
                    <span v-else class="text-xs text-gray-300">-</span>
                  </td>
                  <!-- Téléphone -->
                  <td class="px-5 py-3">
                    <div v-if="supplier.phone" class="flex items-center gap-2">
                      <i class="fas fa-phone text-gray-300 text-[9px]"></i>
                      <span class="text-xs text-gray-600">{{ supplier.phone }}</span>
                    </div>
                    <span v-else class="text-xs text-gray-300">-</span>
                  </td>
                  <!-- Statut -->
                  <td class="px-5 py-3 text-center">
                    <span :class="[
                      'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold',
                      supplier.active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                    ]">
                      <span :class="[
                        'w-1.5 h-1.5 rounded-full',
                        supplier.active ? 'bg-emerald-500' : 'bg-gray-400'
                      ]"></span>
                      {{ supplier.active ? 'Actif' : 'Inactif' }}
                    </span>
                  </td>
                  <!-- Date -->
                  <td class="px-5 py-3">
                    <span class="text-xs text-gray-500">{{ formatDate(supplier.created_at) }}</span>
                  </td>
                  <!-- Action -->
                  <td class="px-5 py-3 text-right">
                    <i class="fas fa-chevron-right text-[9px] text-gray-300 group-hover:text-primary transition-colors"></i>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <PaginationComponent
            :currentPage="pagination.page"
            :perPage="Number(pagination.per_page)"
            :total="pagination.total"
            :to="pagination.to || Math.min(pagination.page * Number(pagination.per_page), pagination.total)"
            :showPageNumbers="true"
            :maxVisiblePages="5"
            @page-change="goToPage"
          />
        </div>
      </div>
    </div>

    <CreateSupplierModal :show="showCreateModal" @close="showCreateModal = false" @created="onSupplierCreated" />
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue'
import supplierService from '@/services/supplierService'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import CreateSupplierModal from './CreateSupplierModal.vue'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'SuppliersView',
  components: { CreateSupplierModal, PaginationComponent },
  setup() {
    const loading = ref(false)
    const suppliers = ref([])
    const router = useRouter()
    const showCreateModal = ref(false)
    const filters = ref({
      search: '',
      per_page: 10
    })
    const pagination = ref({
      page: 1,
      per_page: 10,
      total: 0,
      from: 0,
      to: 0
    })
    let debounceTimer = null

    const fetchSuppliers = async () => {
      try {
        loading.value = true
        const res = await supplierService.getSuppliers({
          search: filters.value.search || undefined,
          per_page: filters.value.per_page,
          page: pagination.value.page,
          sort_by: 'name',
          sort_direction: 'asc'
        })
        const data = res.data
        if (Array.isArray(data)) {
          suppliers.value = data
          pagination.value.total = data.length
          pagination.value.per_page = filters.value.per_page
          pagination.value.from = data.length ? 1 : 0
          pagination.value.to = data.length
        } else {
          suppliers.value = data.data || data.items || []
          pagination.value.total = Number(data.total || (data.meta && data.meta.total) || suppliers.value.length) || 0
          pagination.value.per_page = Number(data.per_page || (data.meta && data.meta.per_page) || filters.value.per_page) || 10
          pagination.value.page = Number(data.current_page || (data.meta && data.meta.current_page) || pagination.value.page) || 1
          pagination.value.from = Number(data.from || (data.meta && data.meta.from) || ((pagination.value.page - 1) * pagination.value.per_page + 1)) || 0
          pagination.value.to = Number(data.to || (data.meta && data.meta.to) || Math.min(pagination.value.page * pagination.value.per_page, pagination.value.total)) || 0
        }
      } catch (e) {
        console.error('Erreur chargement fournisseurs:', e)
        suppliers.value = []
      } finally {
        loading.value = false
      }
    }

    const debouncedFetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        pagination.value.page = 1
        fetchSuppliers()
      }, 300)
    }

    const clearSearch = () => {
      filters.value.search = ''
      pagination.value.page = 1
      fetchSuppliers()
    }

    const goToPage = (page) => {
      pagination.value.page = page
      fetchSuppliers()
    }

    const onAddSupplier = () => {
      showCreateModal.value = true
    }

    const onSupplierCreated = () => {
      showCreateModal.value = false
      pagination.value.page = 1
      fetchSuppliers()
    }

    const goToDetails = (id) => {
      router.push({ name: 'supplier-detail', params: { id } })
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    fetchSuppliers()

    return {
      loading,
      suppliers,
      filters,
      pagination,
      fetchSuppliers,
      debouncedFetch,
      clearSearch,
      goToPage,
      onAddSupplier,
      onSupplierCreated,
      showCreateModal,
      formatDate,
      goToDetails,
      getInitials
    }
  }
})
</script>
